/**
 * Configuración del workflow Residuos
 *
 * Los "tipos de operación" son en realidad los TIPOS DE RESIDUO:
 * - Peligrosos
 * - No Peligrosos
 * - Limpieza de Playa
 * - Envío desde Bodega (NUEVO)
 */

import { RegisterWorkflow } from './RegisterWorkflow';
import { ResiduosWarehouseStockSendWorkflow } from './ResiduosWarehouseStockSendWorkflow';
import { ResiduosWarehouseGuideLinesSendWorkflow } from './ResiduosWarehouseGuideLinesSendWorkflow';
import * as Yup from 'yup';
import { residue_config } from '../../../../../config';

export const config = {
	key: 'residuos',
	shortLabel: 'Residuos',
	label: 'Residuos',
	description: 'Guías de despacho para residuos',

	// Tipos de operación = Tipos de Residuo
	operationTypes: [
		{
			value: 'peligrosos',
			label: 'Peligrosos',
			residue_type_id: residue_config.REACT_APP_RESIDUE_TYPE_PELIGROSOS,
			workflow: 'register',
		},
		{
			value: 'no_peligrosos',
			label: 'No Peligrosos',
			residue_type_id: residue_config.REACT_APP_RESIDUE_TYPE_NO_PELIGROSOS,
			workflow: 'register',
		},
		{
			value: 'limpieza_playa',
			label: 'Limpieza de Playa',
			residue_type_id: residue_config.REACT_APP_RESIDUE_TYPE_BEACH_CLEANING,
			residue_category_id:
				residue_config.REACT_APP_RESIDUE_CATEGORY_BEACH_CLEANING,
			workflow: 'register',
		},
		// NUEVOS: Envío desde Bodega
		{
			value: 'envio_bodega_peligrosos',
			label: 'Envío desde Bodega - Peligrosos',
			residue_type_id: residue_config.REACT_APP_RESIDUE_TYPE_PELIGROSOS,
			workflow: 'warehouseSendV2',
		},
		{
			value: 'envio_bodega_no_peligrosos',
			label: 'Envío desde Bodega - No Peligrosos',
			residue_type_id: residue_config.REACT_APP_RESIDUE_TYPE_NO_PELIGROSOS,
			workflow: 'warehouseGuideLinesSend',
		},
	],

	// Campos adicionales que este workflow necesita
	// NOTA: Los campos residue_type_id, destiny_type_id, warehouse_id, etc.
	// están definidos en ResiduosDestinoSection y ResiduosItemsSection
	additionalFields: {
		getInitialValues: () => ({
			residuos_operation_type: '',
		}),
		getValidationSchema: () => ({
			residuos_operation_type: Yup.string().required(
				'Seleccione tipo de operación',
			),
		}),
	},
};

/**
 * Obtiene el workflow específico según el tipo de operación
 */
export const getWorkflowForOperation = operationType => {
	const opConfig = config.operationTypes.find(
		op => op.value === operationType,
	);
	return opConfig?.workflow || 'register';
};

/**
 * Ejecutor del workflow
 * Delega al workflow específico según el tipo de operación
 */
export const execute = async (dispatch, dteResult, formValues, onProgress) => {
	const { residuos_operation_type } = formValues;

	// Obtener workflow específico
	const workflowType = getWorkflowForOperation(residuos_operation_type);

	// Ejecutar workflow correspondiente
	switch (workflowType) {
		case 'warehouseSendV2':
			return await ResiduosWarehouseStockSendWorkflow.execute(
				dispatch,
				dteResult,
				formValues,
				onProgress,
			);
		case 'warehouseGuideLinesSend':
			return await ResiduosWarehouseGuideLinesSendWorkflow.execute(
				dispatch,
				dteResult,
				formValues,
				onProgress,
			);
		case 'register':
		default:
			return await RegisterWorkflow.execute(
				dispatch,
				dteResult,
				formValues,
				onProgress,
			);
	}
};

/**
 * Obtiene los pasos de progreso que ejecutará el workflow
 */
export const getProgressSteps = formValues => {
	const workflowType = getWorkflowForOperation(
		formValues.residuos_operation_type,
	);

	if (
		workflowType === 'warehouseSendV2' ||
		workflowType === 'warehouseGuideLinesSend'
	) {
		return [
			{ id: 'wf_pdf', label: 'Generando PDF de la guía' },
			{ id: 'wf_upload', label: 'Subiendo documento' },
			{ id: 'wf_consolidate', label: 'Consolidando guías de bodega' },
			{ id: 'wf_warehouse', label: 'Registrando en listado unificado' },
		];
	}

	// Pasos por defecto (register)
	return [
		{ id: 'wf_pdf', label: 'Generando PDF de la guía' },
		{ id: 'wf_upload', label: 'Subiendo documento' },
		{ id: 'wf_register', label: 'Registrando guía de residuos' },
		{ id: 'wf_warehouse', label: 'Registrando en listado unificado' },
	];
};

/**
 * Obtiene los pasos de rollback para este workflow
 */
export const getRollbackSteps = formValues => {
	const workflowType = getWorkflowForOperation(
		formValues.residuos_operation_type,
	);

	if (
		workflowType === 'warehouseSendV2' ||
		workflowType === 'warehouseGuideLinesSend'
	) {
		return [
			{
				id: 'rb_annul_guide',
				label: 'Anulando listado unificado y/o guía de residuos',
			},
		];
	}

	return [{ id: 'rb_annul_guide', label: 'Anulando guía interna' }];
};

/**
 * Validación pre-submit
 */
export const preValidate = async formValues => {
	const errors = [];
	const workflowType = getWorkflowForOperation(
		formValues.residuos_operation_type,
	);

	if (workflowType === 'warehouseSendV2') {
		const warehouseItems =
			formValues.items?.filter(
				item => item?._module_vars?._is_warehouse_stock_send,
			) || [];

		if (warehouseItems.length === 0) {
			errors.push('Debe seleccionar al menos un slot de stock para enviar.');
		}

		const itemsWithoutKg = warehouseItems.filter(item => {
			const mv = item?._module_vars || {};
			return !(Number(mv.kilograms_to_send) > 0);
		});

		if (itemsWithoutKg.length > 0) {
			errors.push(
				'Todos los slots deben tener especificados los kilogramos a enviar.',
			);
		}
	} else if (workflowType === 'warehouseGuideLinesSend') {
		const lineItems =
			formValues.items?.filter(
				item => item?._module_vars?._is_warehouse_guide_lines_send,
			) || [];

		if (lineItems.length === 0) {
			errors.push(
				'Debe seleccionar al menos una línea de guía recepcionada para enviar.',
			);
		}

		const itemsWithoutKg = lineItems.filter(item => {
			const mv = item?._module_vars || {};
			const kgs = mv.individual_kilograms || [];
			return !kgs.some(v => Number(v) > 0);
		});

		if (itemsWithoutKg.length > 0) {
			errors.push(
				'Todas las líneas deben tener kilogramos a enviar mayores a cero.',
			);
		}
	} else {
		// Validación estándar para registro normal
		const residueItems =
			formValues.items?.filter(item => item._module_vars?.residue_id) || [];

		if (residueItems.length === 0) {
			errors.push('Debe agregar al menos un residuo');
		}

		// Verificar totales desde _module_vars
		const totalKilograms = residueItems.reduce((sum, item) => {
			return sum + (Number(item._module_vars?.kilograms_total) || 0);
		}, 0);

		if (totalKilograms <= 0) {
			errors.push('El total de kilogramos debe ser mayor a 0');
		}
	}

	return {
		valid: errors.length === 0,
		errors,
	};
};
